import React from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'react-emotion'
import moment from 'moment'
import { Badge, color, fontWeight, spacer, typography } from 'stylist'
import ProgressBar from 'components/ProgressBarV2'
import FilmReelIcon from 'components/icons/FilmReel'
import ArchiveUserVideoMutation from 'components/mutations/ArchiveUserVideo'
import TileContainer from './TileContainer'
import { Error as ErrorTile } from './VideoUploadProgressTile'

// Logistic function that maps times in hours to the 0-100 range, with 1/2 hour at
// the midpoint
const logisticProgress = x => 100 / (1 + Math.pow(Math.E, -5 * (x - 0.5)))
const oneHourMS = 60 * 60 * 1000
const fakeProgressSince = startTime =>
  logisticProgress((moment() - startTime) / oneHourMS)

const tileBackgroundPadding = ({ padding, theme }) =>
  padding &&
  css`
    padding: ${spacer(6, { theme })} ${spacer(4, { theme })};
  `

const TileBackground = styled.div`
  margin-bottom: calc(${spacer(5)} * 2 + 16px);
  ${tileBackgroundPadding};
  min-height: 144px;
  background-repeat: no-repeat;
  background-size: cover;
  ${typography('body2Medium')}
  background-image: ${({ thumbnailUrl }) =>
    thumbnailUrl ? `url(${thumbnailUrl})` : null};
`

const TileContents = styled.div`
  position: absolute;
  bottom: 0;
  background-color: ${({ backgroundColor = 'ink', theme }) =>
    color(backgroundColor, { theme })};
  color: ${color('white')};
  width: 100%;
  padding: ${spacer(5)};
  transition: height 0.5s;
  height: calc(${spacer(5)} * 2 + 16px);
  overflow: hidden;

  ${({ isForest, theme }) =>
    isForest &&
    css`
      background-color: ${color('forest', { theme })};
      :hover {
        height: 25%;
      }
    `};
`

const TileTitle = styled.p`
  ${fontWeight('bold')};
  white-space: nowrap;
  width: 85%;
  overflow-x: hidden;
  text-overflow: ellipsis;
`

const ProcessingDescription = styled.div`
  margin-top: ${spacer(2)};
  ${typography('body2')};
`

const FilmReelContainer = styled.div`
  position: absolute;
  right: 0;
`

const FilmReel = () => (
  <FilmReelContainer>
    <FilmReelIcon
      width="201px"
      height="115px"
      viewBox="0 0 201 115"
      version="1.1"
    />
  </FilmReelContainer>
)

const ProcessingVideoTile = ({ insertedAt, title }) => (
  <TileContainer processing>
    <TileBackground padding>
      <i>Processing Highlight Reel</i>
      <ProgressBar
        progress={fakeProgressSince(moment.utc(insertedAt))}
        gradientColor="bluePink"
      />
      <ProcessingDescription>
        Hold tight, we're working on something special for you. We will notify
        you when it's ready.
      </ProcessingDescription>
    </TileBackground>
    <TileContents backgroundColor="purple">
      <TileTitle>{title}</TileTitle>
    </TileContents>
  </TileContainer>
)

const NewBadge = styled(Badge)`
  position: absolute;
  top: 25px;
  right: 10px;
  color: ${({ theme }) => color('ink', { theme })};
`

const VideoTile = ({
  id,
  insertedAt,
  clips,
  title,
  lastViewedAt,
  thumbnailUrl
}) =>
  clips.edges && clips.edges.length ? (
    <Link to={`/videos/${id}`}>
      <TileContainer isVideo>
        <TileBackground thumbnailUrl={thumbnailUrl}>
          {thumbnailUrl ? null : <FilmReel />}
          <TileContents>
            <TileTitle>{title}</TileTitle>

            {!lastViewedAt && (
              <NewBadge solid color={'lightBrandGreen'}>
                NEW
              </NewBadge>
            )}
          </TileContents>
        </TileBackground>
      </TileContainer>
    </Link>
  ) : moment().diff(moment(insertedAt), 'days') > 1 ? (
    <TileContainer failed>
      <ArchiveUserVideoMutation videoId={id}>
        {({ archiveUserVideo }) => (
          <ErrorTile
            supportLink="https://focusvision.zendesk.com/hc/en-us/articles/360013064433"
            title="Highlight Reel Failed"
            supportLinkLabel="Why did my highlight reel fail?"
            file={{ name: title }}
            handleRemove={() => archiveUserVideo(id)}
          />
        )}
      </ArchiveUserVideoMutation>
    </TileContainer>
  ) : (
    <ProcessingVideoTile {...{ id, insertedAt, clips, title }} />
  )

export default VideoTile



// WEBPACK FOOTER //
// ./src/routes/videos/components/VideoTile.js